// SNOWCLUB-SOURCING-RUN001-ACTIVATION-FE-20261024 — PURCHASE TRUTH, read from the stable backend activation DTO.
//
// WHAT DECIDES WHETHER A BUY/ADD/BOOK CONTROL IS LIVE. The backend's additive activation contract
// (sibling backend-baseline, read-only here):
//   · GET /api/marketplace/listings  — each item adds `saleState: 'catalogue' | 'bookable'` and
//     `purchase: { purchasable: boolean, display: 'buy'|'coming'|'from-price'|'listed', reasons: [{scope, code}] }`.
//   · GET /api/marketplace/mountains — each row adds `state: 'listed' | 'live'` (anything but exactly "live" is listed).
//   · GET /api/marketplace/vendors?mountain= — each row adds `sellingEnabled: boolean`.
// The server already ANDs mountain, vendor, product, payment and transport readiness into `purchase.purchasable`. The UI
// re-reads each of those facts and ANDs them AGAIN: exact `purchase.purchasable === true` AND `saleState === "bookable"` AND
// `display === "buy"` AND no reasons AND the listing's mountain is exactly `live` AND the listing's vendor has
// `sellingEnabled === true`. A missing field, a wrong shape, an unreadable endpoint, a duplicated identity or any `false`
// is OFF. OFF never hides a product, a price, a photo or a profile; it only removes the buy/add/book action.
//
// Nothing here falls back to the legacy `sellable`, the legacy ACTIVE flag, or a row count: when the activation reads are
// unavailable, every control is OFF and the payload says the reads were unavailable. That is the honest state.

import { fetchMarketplacePages } from "@/lib/marketplace-pages";

export const PURCHASE_DISPLAYS = ["buy", "coming", "from-price", "listed"] as const;
export type PurchaseDisplay = (typeof PURCHASE_DISPLAYS)[number];
export const PURCHASE_REASON_SCOPES = ["mountain", "vendor", "product", "payment", "transport"] as const;
export type PurchaseReasonScope = (typeof PURCHASE_REASON_SCOPES)[number];
export const SALE_STATES = ["catalogue", "bookable"] as const;
export type SaleState = (typeof SALE_STATES)[number];

export interface PurchaseReason { scope: PurchaseReasonScope; code: string }
/** The listing's `purchase` object, accepted only in its exact published shape. */
export interface PurchaseDto { purchasable: boolean; display: PurchaseDisplay; reasons: PurchaseReason[] }

const rec = (v: unknown): Record<string, unknown> | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null);
const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/** Strict read of `listing.purchase`. Any deviation from the contract (missing object, non-boolean `purchasable`, unknown
 * `display`, reasons not an array of `{scope, code}`) is `null`, which every consumer treats as OFF. */
export function readPurchaseDto(raw: unknown): PurchaseDto | null {
  const p = rec(raw);
  if (!p) return null;
  if (typeof p.purchasable !== "boolean") return null;
  const display = text(p.display);
  if (!(PURCHASE_DISPLAYS as readonly string[]).includes(display)) return null;
  if (!Array.isArray(p.reasons)) return null;
  const reasons: PurchaseReason[] = [];
  for (const r of p.reasons) {
    const o = rec(r);
    const scope = text(o?.scope);
    const code = text(o?.code);
    if (!o || !(PURCHASE_REASON_SCOPES as readonly string[]).includes(scope) || !code) return null;
    reasons.push({ scope: scope as PurchaseReasonScope, code });
  }
  return { purchasable: p.purchasable, display: display as PurchaseDisplay, reasons };
}

/** The listing's `saleState`, or null when absent/unknown (OFF). */
export function readSaleState(raw: unknown): SaleState | null {
  const s = text(raw);
  return (SALE_STATES as readonly string[]).includes(s) ? (s as SaleState) : null;
}

/** The product-level verdict from the listing alone: purchasable only when the server says so in every field it publishes.
 * `display !== "buy"` with `purchasable: true` is a contradiction, and a contradiction is OFF. */
export function listingPurchaseOn(listing: unknown): boolean {
  const l = rec(listing);
  if (!l) return false;
  const dto = readPurchaseDto(l.purchase);
  if (!dto) return false;
  return dto.purchasable === true && dto.display === "buy" && dto.reasons.length === 0 && readSaleState(l.saleState) === "bookable";
}

// ── THE MOUNTAIN AND VENDOR FACTS, read from their own endpoints ───────────────────────────────────────────────────────────

/** `unavailable` is the endpoint not answering or answering outside its contract: nothing is live, and the caller says so. */
export interface MountainActivationRead { status: "ok" | "unavailable"; live: ReadonlySet<string> }
export interface VendorActivationRead { status: "ok" | "unavailable"; sellingEnabled: ReadonlyMap<string, boolean> }

export const ACTIVATION_READ_TIMEOUT_MS = 5000;

async function readJson(url: string, fetchImpl: typeof fetch, timeoutMs: number): Promise<unknown> {
  const signal = AbortSignal.timeout(timeoutMs);
  const deadline = new Promise<never>((_, reject) => {
    signal.addEventListener("abort", () => reject(new Error("activation read timed out")), { once: true });
  });
  const read = async (): Promise<unknown> => {
    const res = await fetchImpl(url, { cache: "no-store", signal });
    if (!res.ok) throw new Error(`activation read ${res.status}`);
    return res.json();
  };
  return Promise.race([read(), deadline]);
}

/** Which mountains the backend publishes as exactly `live`. Rows are keyed by slug; a slug published twice with differing
 * states is ambiguous and never live. A row with any state but the exact string "live" is listed (OFF). */
export function mountainsLiveFrom(data: unknown): ReadonlySet<string> | null {
  if (!Array.isArray(data)) return null;
  const seen = new Map<string, boolean | null>();
  for (const raw of data) {
    const m = rec(raw);
    const slug = text(m?.slug);
    if (!m || !slug) continue;
    const live = m.state === "live";
    seen.set(slug, seen.has(slug) && seen.get(slug) !== live ? null : live);
  }
  const live = new Set<string>();
  for (const [slug, v] of seen) if (v === true) live.add(slug);
  return live;
}

export async function fetchMountainActivation(
  strapiUrl: string | undefined,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = ACTIVATION_READ_TIMEOUT_MS,
): Promise<MountainActivationRead> {
  const base = (strapiUrl ?? "").replace(/\/$/, "");
  if (!base) return { status: "unavailable", live: new Set() };
  try {
    const body = (await readJson(`${base}/api/marketplace/mountains`, fetchImpl, timeoutMs)) as { data?: unknown } | null;
    const live = mountainsLiveFrom(body?.data);
    return live ? { status: "ok", live } : { status: "unavailable", live: new Set() };
  } catch {
    return { status: "unavailable", live: new Set() };
  }
}

/** Which vendors (by documentId) the backend publishes with `sellingEnabled: true`. A documentId published twice is
 * ambiguous and never enabled; a non-boolean flag is OFF. */
export function vendorsSellingFrom(data: unknown): ReadonlyMap<string, boolean> | null {
  if (!Array.isArray(data)) return null;
  const seen = new Map<string, boolean | null>();
  for (const raw of data) {
    const v = rec(raw);
    const id = text(v?.documentId);
    if (!v || !id) continue;
    const on = v.sellingEnabled === true;
    seen.set(id, seen.has(id) ? null : on);
  }
  const out = new Map<string, boolean>();
  for (const [id, v] of seen) out.set(id, v === true);
  return out;
}

export async function fetchVendorActivation(
  strapiUrl: string | undefined,
  mountain: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = ACTIVATION_READ_TIMEOUT_MS,
): Promise<VendorActivationRead> {
  const base = (strapiUrl ?? "").replace(/\/$/, "");
  if (!base) return { status: "unavailable", sellingEnabled: new Map() };
  // every vendor-card page (P1 paging) within the same budget; a partial read is unavailable, never "the rest are not selling"
  const read = await fetchMarketplacePages(base, "/api/marketplace/vendors", { mountain }, { fetchImpl, timeoutMs });
  const map = read.status === "ok" ? vendorsSellingFrom(read.data) : null;
  return map ? { status: "ok", sellingEnabled: map } : { status: "unavailable", sellingEnabled: new Map() };
}

// ── THE AND ───────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** Everything a listing's purchasability is decided against, for one mountain. */
export interface PurchaseContext {
  /** the mountain the feed was read for; a listing must name exactly this slug */
  mountain: string;
  mountains: MountainActivationRead;
  vendors: VendorActivationRead;
}

/** Reads both activation endpoints for one mountain, in parallel. Either failing is reported as unavailable, never guessed. */
export async function fetchPurchaseContext(
  strapiUrl: string | undefined,
  mountain: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = ACTIVATION_READ_TIMEOUT_MS,
): Promise<PurchaseContext> {
  const [mountains, vendors] = await Promise.all([
    fetchMountainActivation(strapiUrl, fetchImpl, timeoutMs),
    fetchVendorActivation(strapiUrl, mountain, fetchImpl, timeoutMs),
  ]);
  return { mountain, mountains, vendors };
}

/** A context in which nothing is purchasable: the honest value when the activation reads did not happen. */
export function offlinePurchaseContext(mountain: string): PurchaseContext {
  return { mountain, mountains: { status: "unavailable", live: new Set() }, vendors: { status: "unavailable", sellingEnabled: new Map() } };
}

/**
 * THE verdict for one listing: ON only when every fact is exactly on.
 *   listing.purchase.purchasable === true  AND display === "buy" AND reasons.length === 0 AND saleState === "bookable"
 *   AND the listing's mountain.slug is the context mountain AND that mountain is published `live`
 *   AND the listing's vendor.documentId is published with sellingEnabled === true.
 * Both activation reads must have answered. Anything else — missing, false, error, ambiguous — is OFF.
 */
export function listingPurchasable(listing: unknown, ctx: PurchaseContext): boolean {
  const l = rec(listing);
  if (!l) return false;
  if (!listingPurchaseOn(l)) return false;
  if (ctx.mountains.status !== "ok" || ctx.vendors.status !== "ok") return false;
  const slug = text(rec(l.mountain)?.slug);
  if (!slug || slug !== ctx.mountain || !ctx.mountains.live.has(slug)) return false;
  const vendorId = text(rec(l.vendor)?.documentId);
  if (!vendorId) return false;
  return ctx.vendors.sellingEnabled.get(vendorId) === true;
}

/** The public status copy for an OFF control. Taxonomy word from the Design SOP's status set, never a reason code. */
export const NOT_SOLD_ONLINE = "Not sold online";
